import React from 'react';

import { useSnackbar } from 'notistack';
import { useForm } from 'react-hook-form';

import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';

import type { TagSchedule } from '~/services/TagsService';

import { useCreateTagSchedule } from '../hooks';

import type { TagScheduleFormData } from './TagScheduleForm';
import TagScheduleForm from './TagScheduleForm';

interface TagScheduleCreateProps {
  tagId?: string;
  open: boolean;
  onClose: () => void;
  onSave: (schedule: Omit<TagSchedule, 'id' | 'tagId'>) => void;
}

export default function TagScheduleCreate({ tagId, open, onClose, onSave }: TagScheduleCreateProps) {
  const { enqueueSnackbar } = useSnackbar();

  const { control, handleSubmit, reset } = useForm<TagScheduleFormData>({
    defaultValues: {
      brands: [],
      activeStartDate: null,
      activeEndDate: null,
    },
  });

  const createTagSchedule = useCreateTagSchedule();

  const onSubmit = async (data: TagScheduleFormData) => {
    if (!data.activeStartDate || !data.activeEndDate) {
      enqueueSnackbar('Please select both active period start and end dates', { variant: 'error' });
      return;
    }

    if (data.activeStartDate && data.activeEndDate && data.activeStartDate > data.activeEndDate) {
      enqueueSnackbar('Active period start date must be before end date', { variant: 'error' });
      return;
    }

    if (tagId) {
      await createTagSchedule.mutateAsync({
        tagId,
        ...data,
      });
      reset();
      onClose();
      return;
    }

    const newSchedule = {
      id: Date.now(),
      brands: data.brands,
      activeStartDate: data.activeStartDate,
      activeEndDate: data.activeEndDate,
    };

    onSave(newSchedule);
    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Schedule</DialogTitle>
      <DialogContent>
        <TagScheduleForm control={control} />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createTagSchedule.isPending} variant="text">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          autoFocus
          disabled={createTagSchedule.isPending}
          startIcon={createTagSchedule.isPending ? <CircularProgress size={16} /> : null}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
}
